import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Banner, ConfirmDialog } from "@v0y4ge/jrni-ui";
import { useUnsavedGuard } from "../../../shell/editor-hooks";
import { registerNavigationGuard, resumeNavigation } from "../../../shell/navigation-guard";
type Tr = (key: string, fallback: string) => string;
type State = { dirty: boolean; pending: boolean };
type Registry = { set: (id: string, value: State | null) => void; leave: (action: () => void) => void };
const DraftContext = createContext<Registry | null>(null);

export function MessageDraftBoundary({ children, t }: { children: ReactNode; t: Tr }) {
  const states = useRef(new Map<string, State>()), action = useRef<(() => void) | null>(null);
  const [confirming, setConfirming] = useState(false), [blocked, setBlocked] = useState(false);
  const guard = useRef((next: () => void): boolean => {
    if ([...states.current.values()].some(s => s.pending)) { setBlocked(true); return true; }
    if (![...states.current.values()].some(s => s.dirty)) return false;
    action.current = next; setConfirming(true); return true;
  });
  const registry = useRef<Registry>({
    set: (id, value) => { if (value) states.current.set(id, value); else states.current.delete(id); if (![...states.current.values()].some(s => s.pending)) setBlocked(false); },
    leave: next => { if (!guard.current(next)) next(); },
  });
  useEffect(() => registerNavigationGuard(next => guard.current(next)), []);
  return <DraftContext.Provider value={registry.current}>
    {blocked ? <Banner tone="warning" size="compact" title={t("messaging.builder.finishSave", "Wait for the save to finish before leaving.")} /> : null}
    {children}
    <ConfirmDialog open={confirming} tone="neutral" title={t("messaging.builder.discardTitle", "Discard unsaved changes?")}
      body={t("messaging.builder.discardBody", "Your edited content has not been saved.")}
      confirmLabel={t("messaging.builder.discard", "Discard changes")}
      cancelLabel={t("messaging.builder.keepEditing", "Keep editing")}
      onCancel={() => { action.current = null; setConfirming(false); }}
      onConfirm={() => { const next = action.current; action.current = null; setConfirming(false); setBlocked(false); if (next) resumeNavigation(next); }}
      testid="msgb-discard-changes" />
  </DraftContext.Provider>;
}

/** Protect modal close, local tab/row selection, SPA navigation and page exit. */
export function useDraftLeaveGuard(dirty: boolean, pending: boolean, _t?: Tr): (action: () => void) => void {
  const registry = useContext(DraftContext), id = useId();
  useUnsavedGuard(dirty || pending);
  useEffect(() => { registry?.set(id, { dirty, pending }); return () => registry?.set(id, null); }, [registry, id, dirty, pending]);
  return action => registry ? registry.leave(action) : action();
}
